import React, { createContext, useContext, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useBrandSelection } from '@/hooks/useBrandSelection';
import { invokeWithTimeout } from '@/utils/edgeFunctionWithTimeout';

export interface Insight {
  id: string;
  insight_type: string;
  query: string | null;
  page: string | null;
  title: string;
  description: string;
  impact_score: number;
  expected_traffic_gain: number | null;
  effort_level: string;
  priority_score: number;
  current_position: number | null;
  expected_position: number | null;
  current_ctr: number | null;
  expected_ctr: number | null;
  created_at: string;
  task_id?: string | null;
}

interface SeoAiInsightsContextType {
  insights: Insight[];
  loading: boolean;
  generating: boolean;
  lastFetchedBrandId: string | null;
  fetchInsights: () => Promise<void>;
  generateInsights: () => Promise<void>;
  deleteInsight: (id: string) => Promise<void>;
  updateInsightTaskId: (insightId: string, taskId: string) => void;
  clearInsights: () => void;
}

const SeoAiInsightsContext = createContext<SeoAiInsightsContextType | undefined>(undefined);

export function SeoAiInsightsProvider({ children }: { children: React.ReactNode }) {
  const { currentBrandId } = useBrandSelection();
  const [insights, setInsights] = useState<Insight[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [lastFetchedBrandId, setLastFetchedBrandId] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchInsights = useCallback(async () => {
    if (!currentBrandId) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('seo_gsc_ai_insights')
        .select('*')
        .eq('brand_id', currentBrandId)
        .order('priority_score', { ascending: false });

      if (error) throw error;
      setInsights(data || []);
      setLastFetchedBrandId(currentBrandId);
    } catch (error) {
      console.error('Error fetching insights:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch insights',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [currentBrandId, toast]);

  const generateInsights = useCallback(async () => {
    if (!currentBrandId) return;

    try {
      setGenerating(true);
      toast({
        title: 'Generating Insights',
        description: 'AI is analyzing your SEO data...',
      });

      const { data, error } = await invokeWithTimeout(
        supabase,
        'gemini-insights',
        {
          body: { brand_id: currentBrandId },
          timeout: 120000,
        }
      );

      if (error) {
        console.error('Function invocation error:', error);
        throw new Error(error?.message || 'Failed to invoke insights function');
      }

      if (!data) {
        throw new Error('No data returned from insights function');
      }

      toast({
        title: 'Success',
        description: `Generated ${data.count || 0} AI-powered insights`,
      });

      await fetchInsights();
    } catch (error) {
      console.error('Error generating insights:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate insights';
      toast({
        title: 'Error Generating Insights',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setGenerating(false);
    }
  }, [currentBrandId, toast, fetchInsights]);

  const deleteInsight = useCallback(async (insightId: string) => {
    try {
      const { error } = await supabase
        .from('seo_gsc_ai_insights')
        .delete()
        .eq('id', insightId);

      if (error) throw error;

      setInsights(prev => prev.filter(insight => insight.id !== insightId));
      
      toast({
        title: 'Deleted',
        description: 'Insight removed',
      });
    } catch (error) {
      console.error('Error deleting insight:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete insight',
        variant: 'destructive',
      });
    }
  }, [toast]);

  const updateInsightTaskId = useCallback((insightId: string, taskId: string) => {
    setInsights(prev => 
      prev.map(insight => 
        insight.id === insightId 
          ? { ...insight, task_id: taskId } 
          : insight
      )
    );
  }, []);

  const clearInsights = useCallback(() => {
    setInsights([]);
    setLastFetchedBrandId(null);
  }, []);

  return (
    <SeoAiInsightsContext.Provider
      value={{
        insights,
        loading,
        generating,
        lastFetchedBrandId,
        fetchInsights,
        generateInsights,
        deleteInsight,
        updateInsightTaskId,
        clearInsights,
      }}
    >
      {children}
    </SeoAiInsightsContext.Provider>
  );
}

export function useSeoAiInsights() {
  const context = useContext(SeoAiInsightsContext);
  if (context === undefined) {
    throw new Error('useSeoAiInsights must be used within a SeoAiInsightsProvider');
  }
  return context;
}
